import {
  createContext,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
} from "react";
import { useConstComponent } from "./useNestedComponent";

/** The context value used for tracking child component values passed upward.
 *
 * This class is instantiated internally, and is exported primarily for `instanceof` checking. */
export class ReverseContext<T> {
  private maps: Map<string, T>[];

  constructor(
    private Context: React.Context<ReverseContext<T> | null>,
    otherMaps: Map<string, T>[]
  ) {
    this.maps = [new Map(), ...otherMaps];
  }

  /** Private method for a child to subscribe to the context with a value. */
  _subscribe(id: string, value: T) {
    for (const map of this.maps) {
      map.set(id, value);
    }
    return () => {
      for (const map of this.maps) {
        map.delete(id);
      }
    };
  }

  /** Private method for generating a sub-context value for intermediate use.
   * Incorporates the parent context's map into the child so that the parent also tracks all values of this child's nested values. */
  _createChild() {
    return new ReverseContext<T>(this.Context, this.maps);
  }

  /** Get the values passed up from the children. */
  values = () => {
    return [...this.maps[0].values()];
  };

  /** The provider component to wrap the children in for the reverse pass-up to work. */
  Provider = ({ children }: { children: React.ReactNode }) => {
    const Context = useConstComponent(this.Context);
    return <Context.Provider value={this}>{children}</Context.Provider>;
  };
}

/** Creates a reverse context.
 *
 * To use a reverse-context:
 * 1. First create it using `createReverseContext`.
 * 2. Then, in the parent component, use the `useReverseContextAsParent` hook.
 * 3. In the parent, wrap the returned children using the <Provider> component
 *    from the instance returned by `useReverseContextAsParent`.
 * 4. In the child components, use the `useReverseContextAsChild` hook,
 *    passing the values from the children.
 * 5. Finally, the value returned from `useReverseContextAsParent` has a `values` method
 *    that, when called, will provide an array of all values passed from the children.
 *    **Warning:** The values in this array are in no guaranteed order. As components
 *    mount and unmount, the order may also change over time.
 *
 * Example:
 *
 * ```tsx
 * // Create a new reverse context
 * const MyReverseContext = createReverseContext<(label:string) => void>();
 *
 * const Parent = () => {
 *   // Use it in the parent
 *   const {Provider, values} = useReverseContextAsParent(MyReverseContext);
 *   // const {Provider, values} = MyReverseContext.useAsParent(); // alternate syntax
 *
 *   // When clicking the button, update all child labels to "world!"
 *   const handleClick = () => {
 *     for(const fn of values()) fn("world!");
 *   };
 *
 *   // Wrap children with <Provider> component
 *   return (
 *     <Provider>
 *       <Child initialLabel="hello 1" />
 *       <Child initialLabel="hello 2" />
 *       <Child initialLabel="hello 3" />
 *       <button onClick={handleClick}>Submit</button>
 *     </Provider>
 *   );
 * };
 *
 * const Child = ({initialLabel = ""}: {initialLabel?: string}) => {
 *   // Example context pass-up use case
 *   const [label, setLabel] = useState(initialLabel);
 *
 *   // Use in the child context
 *   useReverseContextAsChild(MyReverseContext, setLabel);
 *   // MyReverseContext.useAsChild(setLabel); // alternate syntax
 *
 *   // Display the label
 *   return <p>{label}</p>;
 * };
 * ```
 *
 * Reverse contexts can also be used in intermediate stages as well. This is necessary
 * when a root component needs to obtain access to the values of all child components,
 * whereas one of its children with descendants must access the subset of these values
 * that come from the components in its own subtree.
 *
 * See `useReverseContextAsIntermediate` for details.
 */
export const createReverseContext = <T,>() => {
  const context = createContext<ReverseContext<T> | null>(null);

  return Object.assign(context, {
    useAsParent(): ReverseContext<T> {
      return useReverseContextAsParent<T>(context);
    },
    useAsIntermediate(value?: T | null): ReverseContext<T> {
      return useReverseContextAsIndermediate<T>(context, value);
    },
    useAsChild(value?: T | null): void {
      return useReverseContextAsChild<T>(context, value);
    },
  });
};

/** Use a reverse context in the parent component.
 *
 * The returned value includes a `<Provider>` component to wrap children in,
 * and a values function to get the values provided by all children. */
export const useReverseContextAsParent = <T,>(
  Context: React.Context<ReverseContext<T> | null>
) => {
  return useMemo(() => new ReverseContext<T>(Context, []), [Context]);
};

/** Use a reverse context in a component that is both a descendant of another
 * component using `useReverseContextAsParent` but also a parent of other
 * components that pass up values via `useReverseContextAsChild`.
 *
 * When using `useReverseContextAsParent`, the component takes exclusive
 * ownership of all values passed up by its children. Thus, an ancestor of this
 * component that also uses `useReverseContextAsParent` will not receive the
 * values of the children of this component.
 *
 * When using `useReverseContextAsIntermediate`, the component receives the
 * values of its children, but those values are also passed up further to any
 * parent component using `useReverseContextAsParent` or
 * `useReverseContextAsIntermediate`.
 *
 * It is not an error to use `useReverseContextAsIntermediate` in a component
 * that does not have a parent using `useReverseContextAsParent`. The value
 * provided (if any) will simply be discarded, and the call will otherwise act
 * as if it were a call to `useReverseContextAsParent`.
 */
export const useReverseContextAsIndermediate = <T,>(
  Context: React.Context<ReverseContext<T> | null>,
  value?: T | null
) => {
  useReverseContextAsChild<T>(Context, value);

  const parent = useContext(Context);

  return useMemo(
    () => parent?._createChild() ?? new ReverseContext<T>(Context, []),
    [parent, Context]
  );
};

/** Use a reverse context in the child component, providing a value to pass upward.
 *
 * Calling `useReverseContextAsChild` in a component that is not a child of a component
 * that uses `useReverseContextAsParent` will simply have no effect. */
export const useReverseContextAsChild = <T,>(
  Context: React.Context<ReverseContext<T> | null>,
  value?: T | null
) => {
  const id = useId();
  const ctx = useContext(Context);

  useLayoutEffect(() => {
    if (!ctx || value == null) return;

    return ctx._subscribe(id, value);
  }, [ctx, id, value]);
};
